import React from 'react';
import dayjs from 'dayjs';

import { WeekType } from 'types';
import { Flex } from 'common/flex';
import { PADDING } from 'constants/sizes';
import { Button, IconButton } from 'common/buttons';

import { CalendarDay } from './day';
import { CalendarModalProps } from '../types';

import { Container } from './styles';
import { generateDateRows } from './helpers';

const getCurrentWeek = () => {
  return {
    startDate: dayjs().startOf('week').unix(),
    endDate: dayjs().endOf('week').unix(),
  } as WeekType;
};

export const CalendarModal = ({
  week,
  onApply,
  onClose,
}: CalendarModalProps) => {
  const [currentMonth, setCurrentMonth] = React.useState<number>(
    week?.startDate ?? getCurrentWeek().startDate
  );
  const [selectedWeek, setSelectedWeek] = React.useState<WeekType>(
    week ?? getCurrentWeek()
  );

  React.useEffect(() => {
    setCurrentMonth(selectedWeek.startDate);
  }, [selectedWeek]);

  const onNextMonth = React.useCallback(() => {
    setCurrentMonth(
      dayjs
        .unix(currentMonth ?? selectedWeek.startDate)
        .add(1, 'M')
        .unix()
    );
  }, [selectedWeek, currentMonth]);

  const onPrevMonth = React.useCallback(() => {
    setCurrentMonth(
      dayjs
        .unix(currentMonth ?? selectedWeek.startDate)
        .subtract(1, 'M')
        .unix()
    );
  }, [selectedWeek, currentMonth]);

  const handleApply = React.useCallback(() => {
    onApply(selectedWeek);
  }, [onApply, selectedWeek, week]);

  return (
    <Container>
      <div className="current-month-container">
        <IconButton icon="chevron-left" onClick={onPrevMonth} />
        <h3>
          {dayjs
            .unix(currentMonth ?? selectedWeek.startDate)
            .format('MMMM YYYY')}
        </h3>
        <IconButton icon="chevron-right" onClick={onNextMonth} />
      </div>

      <Flex flex={1} flexDirection="column">
        <div className="weekdays-container">
          {['Sun', 'Mon', 'Thu', 'Wed', 'Tue', 'Fri', 'Sat'].map((weekday) => {
            return (
              <div className="weekday" key={`weekday-block-${weekday}`}>
                <h5>{weekday}</h5>
              </div>
            );
          })}
        </div>

        <Flex flexDirection="column" gap={PADDING.smallest}>
          {generateDateRows(currentMonth ?? selectedWeek.startDate).map(
            (row, index) => {
              return (
                <Flex
                  flexDirection="row"
                  key={`week-${index}`}
                  justifyContent="space-between"
                >
                  {row.map((date, dateIndex) => {
                    return (
                      <CalendarDay
                        date={date}
                        onClick={setSelectedWeek}
                        key={`date-${dateIndex}`}
                        currentMonth={currentMonth}
                        selectedFromDate={selectedWeek.startDate}
                      />
                    );
                  })}
                </Flex>
              );
            }
          )}
        </Flex>
      </Flex>

      <div className="buttons-container">
        <Button
          title="Cancel"
          variant="stroke"
          onClick={onClose}
          palette="secondary"
        />
        <Button
          title="Apply"
          variant="default"
          palette="primary"
          onClick={handleApply}
        />
      </div>
    </Container>
  );
};
